// ** Modules
import { useId, useState } from 'react';

// ** UI
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';

// ** Types
import type { SxProps, Theme } from '@mui/material/styles';
import type { DashboardProfileSection } from './types';

// ** Shared
import {
	DashboardBodyText,
	DashboardCtaLink,
	DashboardPanel,
	DashboardProfileValue,
	getProfileIcon,
	getProfileSummaryLines
} from './shared';

type DashboardProfilePanelProps = {
	profile: DashboardProfileSection;
	sx?: SxProps<Theme>;
};

const DashboardProfilePanel = ({ profile, sx }: DashboardProfilePanelProps) => {
	const theme = useTheme();
	const headingId = useId();
	const summaryLines = getProfileSummaryLines(profile);
	const [isMobileExpanded, setIsMobileExpanded] = useState(false);

	const chevronButtonSx: SxProps<Theme> = {
		p: 0.75,
		borderRadius: 999,
		color: 'text.secondary',
		backgroundColor: 'transparent',
		transition: 'background-color 180ms ease, transform 180ms ease',
		'&:hover': {
			backgroundColor: (theme: Theme) => alpha(theme.palette.common.black, 0.06)
		},
		'&:active': { transform: 'scale(0.97)' },
		'&:focusVisible': {
			outline: (theme: Theme) => `2px solid ${alpha(theme.palette.common.black, 0.35)}`,
			outlineOffset: '2px'
		},
		'@media (prefers-reduced-motion: reduce)': { transition: 'none' }
	};

	// Shared detail list rendered by both mobile (inside Collapse) and desktop
	const profileItems = (
		<Box
			component="dl"
			sx={{
				m: 0,
				display: 'flex',
				flexDirection: 'column',
				gap: { xs: 4, md: 6 }
			}}
		>
			{profile.items.map((item) => (
				<Box
					key={item.id}
					component="div"
					sx={{
						display: 'flex',
						alignItems: 'flex-start',
						gap: { xs: 4, md: 6 }
					}}
				>
					<Box
						sx={{
							color: theme.palette.primary.main,
							flexShrink: 0,
							lineHeight: 0,
							pt: '2px'
						}}
					>
						{getProfileIcon(item.icon)}
					</Box>
					<Box sx={{ minWidth: 0 }}>
						<DashboardBodyText component="dt">{item.label}</DashboardBodyText>
						<DashboardBodyText component="dd" sx={{ mt: 0.5 }}>
							<DashboardProfileValue value={item.value} />
						</DashboardBodyText>
					</Box>
				</Box>
			))}
		</Box>
	);

	return (
		// Outer Box carries only the grid-placement sx so both layouts share it
		<Box sx={[{ minWidth: 0, display: 'flex', flexDirection: 'column' }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}>
			{/* ─── MOBILE LAYOUT (hidden on md+) ─── */}
			<Box
				component="section"
				aria-labelledby={headingId}
				sx={{
					display: { xs: 'flex', md: 'none' },
					flexDirection: 'column',
					backgroundColor: 'common.white',
					borderRadius: '8px',
					p: 4,
					pb: 6
				}}
			>
				{/* Header: title + chevron, space-between */}
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between'
					}}
				>
					<Typography id={headingId} variant="panel-heading" component="h2" sx={{ m: 0, color: 'common.black' }}>
						{profile.title}
					</Typography>

					<IconButton
						aria-label={isMobileExpanded ? 'Profil ausblenden' : 'Profil einblenden'}
						aria-expanded={isMobileExpanded}
						onClick={() => setIsMobileExpanded((v) => !v)}
						disableRipple
						sx={chevronButtonSx}
					>
						<ExpandMoreIcon
							sx={{
								transform: isMobileExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
								transition: 'transform 260ms cubic-bezier(0.22, 1, 0.36, 1)'
							}}
						/>
					</IconButton>
				</Box>

				{/* Summary lines — always visible, 32px below header */}
				{summaryLines.length ? (
					<Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
						{summaryLines.map((line) => (
							<DashboardBodyText key={line}>{line}</DashboardBodyText>
						))}
					</Box>
				) : null}

				{/* Collapsible detail items */}
				<Collapse
					in={isMobileExpanded}
					timeout={{ enter: 320, exit: 220 }}
					easing={{
						enter: 'cubic-bezier(0.22, 1, 0.36, 1)',
						exit: 'cubic-bezier(0.4, 0, 1, 1)'
					}}
					collapsedSize="0px"
				>
					<Box sx={{ mt: 8 }}>{profileItems}</Box>
				</Collapse>

				{/* CTA — always visible, 32px from whatever is above it */}
				{profile.cta ? (
					<Box sx={{ mt: 8 }}>
						<DashboardCtaLink cta={profile.cta} />
					</Box>
				) : null}
			</Box>

			{/* ─── DESKTOP LAYOUT (hidden on xs) ─── */}
			<DashboardPanel
				title={profile.title}
				cta={profile.cta}
				sx={{ display: { xs: 'none', md: 'flex' }, flex: { md: '1 1 auto' } }}
			>
				{profileItems}
			</DashboardPanel>
		</Box>
	);
};

export default DashboardProfilePanel;
